import { IndexedElementPool } from './indexed-element-pool';
import getString from './get-string';

/**
 * KatalTableSelection encapsulates all the logic behind the checkbox selection feature of the data
 * table. It will maintain the state of which rows are selected and provides an API to manipulate
 * that state. Whenever the table's rows change the selection state is reset. All methods are
 * expected to be proxied through from the table so all clients call these methods as if they were
 * methods of the table.
 */
export class KatalTableSelection {
  constructor(table) {
    this._table = table;
    this._selectedRows = {};
    this._checkBoxPool = new IndexedElementPool('kat-checkbox');
    this._attachedRowFormatter = this._rowFormatter.bind(this);

    this._table.addRowFormatter(this._attachedRowFormatter);
  }

  /**
   * Returns true if the given row index is selected.
   * @param {number} rowIndex The index of the row to check.
   * @returns {boolean}
   * @katalmethod
   */
  isRowSelected(rowIndex) {
    return !!this._selectedRows[rowIndex];
  }

  /**
   * Returns true if all rows in the table are selected.
   * @returns {boolean}
   * @katalmethod
   */
  isAllSelected() {
    // For all to be checked all visible rows must be in the respective
    // state.
    const rowData = this._table.rowData || [];
    return rowData.every(
      (rowData, index) =>
        index in this._selectedRows && this._selectedRows[index]
    );
  }

  /**
   * A simple object containing the index and data of row.
   * @typedef {Object} RowStruct
   * @property {number} index The index of the row.
   * @property {object} data The data of the row.
   */
  /**
   * Returns a list of all selected rows both their indexes and their data.
   * @returns {RowStruct[]}
   * @katalmethod
   */
  getSelectedRows() {
    return Object.keys(this._selectedRows)
      .filter(rowIndex => {
        return this._selectedRows[rowIndex];
      })
      .map(rowIndex => {
        return {
          index: rowIndex,
          data: this._table.rowData[rowIndex],
        };
      });
  }

  /**
   * Sets the selection state to only the list passed. Each row index passed is selected and
   * every other row is unselected.
   * @param {number[]} rowIndices An array of row indexes.
   * @katalmethod
   */
  setSelectedRows(rowIndices) {
    if (!Array.isArray(rowIndices)) {
      throw new Error(
        'KatalDataTable:setSelectedRows - expect rowIndices to be an array'
      );
    }

    this._selectedRows = {};
    rowIndices.forEach(index => {
      this._selectedRows[index] = true;
    });

    this._dispatchSelectedChanged();
  }

  /**
   * Selects the row at the given rowIndex. Dispatches an event for the state change and for the
   * selectRow operation if was done by a click.
   * @param {number} rowIndex
   * @param {boolean} dispatchEvent If true the selectRow event is fired.
   * @katalmethod
   */
  selectRow(rowIndex, dispatchEvent = false) {
    this._selectedRows[rowIndex] = true;

    this._rowUpdate(rowIndex);
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('selectRow', {
          bubbles: true,
          detail: { rowIndex },
        })
      );
    }
  }

  /**
   * Unselects the row at the given rowIndex. Dispatches an event for the state change and for the
   * unselectRow operation if was done by a click.
   * @param {number} rowIndex
   * @param {boolean} dispatchEvent If true the unselectRow event is fired.
   * @katalmethod
   */
  unselectRow(rowIndex, dispatchEvent = false) {
    this._selectedRows[rowIndex] = false;

    this._rowUpdate(rowIndex);
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('unselectRow', {
          bubbles: true,
          detail: { rowIndex },
        })
      );
    }
  }

  /**
   * Causes the table to update its header and just the row that was just selected. Dispatches the selection changed
   * event as well.
   * @ignore
   */
  _rowUpdate(rowIndex) {
    this._table.updateRow(rowIndex);

    this._table.deferredUpdateWrapper(() => {
      this._table.updateHeader();
      this._dispatchSelectedChanged();
    });
  }

  /**
   * Selects all rows. Dispatches an event for the state change and for the selectAll operation
   * if was done by a click.
   * @param {boolean} dispatchEvent If true the selectAll event is fired.
   * @katalmethod
   */
  selectAll(dispatchEvent = false) {
    const rowData = this._table.rowData || [];
    for (let i = 0; i < rowData.length; i++) {
      this._selectedRows[i] = true;
    }

    this._tableUpdate();
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('selectAll', { bubbles: true })
      );
    }
  }

  /**
   * Unselects all rows. Dispatches an event for the state change and for the unselectRow operation
   * if was done by a click.
   * @param {boolean} dispatchEvent If true the unselectAll event is fired.
   * @katalmethod
   */
  unselectAll(dispatchEvent = false) {
    this._selectedRows = {};

    this._tableUpdate();
    if (dispatchEvent) {
      this._table.dispatchEvent(
        new CustomEvent('unselectAll', { bubbles: true })
      );
    }
  }

  /**
   * Causes the table to update its header and all the rows. Dispatches the selection changed
   * event as well.
   * @ignore
   */
  _tableUpdate() {
    // Use special optimized function to update just the row elements and checkbox cells.
    this._table.updateForMassSelectionChange();

    this._dispatchSelectedChanged();
  }

  /**
   * Dispatches the "selectionChanged" event from the table. Provides the list of currently
   * selected rows.
   * @ignore
   */
  _dispatchSelectedChanged() {
    this._table.dispatchEvent(
      new CustomEvent('selectionChanged', {
        bubbles: true,
        detail: { selectedRows: this.getSelectedRows() },
      })
    );
  }

  /**
   * Creates a new column configuration object for the checkbox column. Contains the header and
   * cell formatters. They create checkbox elements and attach event handlers to them. Their state
   * is managed by making sure the table updates and rerenders the formatters.
   *
   * @returns {ColumnConfiguration} A column configuration for the checkbox column.
   */
  createCheckboxColumn() {
    return {
      headerFormatter: this._headerFormatter.bind(this),
      formatter: this._cellFormatter.bind(this),
      useHeader: false,
    };
  }

  /**
   * Expected to be called by the table each time the underlying row data of the table is changed.
   * Used to reset the selection state.
   */
  onRowDataChanged() {
    this._selectedRows = {};
  }

  /**
   * The formatter used to create the checkbox in the header that will select or unselect all
   * rows.
   * @ignore
   */
  _headerFormatter(lastElement) {
    const element = lastElement
      ? lastElement
      : this._checkBoxPool.getElement(-1);
    let checked = false;
    let indeterminate = false;

    let allUnchecked = true;
    let allChecked = true;
    // For all to be checked or unchecked all visible rows must be in the respective
    // state.
    const rowData = this._table.rowData || [];
    for (let i = 0; i < rowData.length; i++) {
      if (i in this._selectedRows && this._selectedRows[i]) {
        allUnchecked = false;
      } else {
        allChecked = false;
      }
    }

    if (allUnchecked) {
      checked = false;
    } else if (allChecked) {
      checked = true;
    } else {
      indeterminate = true;
    }
    element.checked = checked;
    element.indeterminate = indeterminate;

    let label = '';
    if (checked) {
      label = getString('selectAllCheckboxAllLabel');
    } else if (indeterminate) {
      label = getString('selectAllCheckboxSomeLabel');
    } else {
      label = getString('selectAllCheckboxNoneLabel');
    }
    element.setAttribute('kat-aria-label', label);

    if (!lastElement) {
      element.addEventListener('change', evt => {
        if (evt.detail.checked) {
          this.selectAll(true);
        } else {
          this.unselectAll(true);
        }
      });
    }

    return {
      element,
      attributes: { class: 'kat-checkbox-column' },
    };
  }

  /**
   * The formatter used to create the checkboxes for each row.
   *
   * @ignore
   */
  _cellFormatter(rowIndex, rowData, lastElement) {
    const element = lastElement
      ? lastElement
      : this._checkBoxPool.getElement(rowIndex);
    element.checked = !!this._selectedRows[rowIndex];

    if (!lastElement) {
      const label = getString('rowCheckboxLabel', {
        rowIndex: rowIndex + 1,
      });
      element.setAttribute('kat-aria-label', label);
      element.addEventListener(
        'change',
        this._checkBoxHandler.bind(this, rowIndex)
      );
    }

    return {
      element,
      attributes: { class: 'kat-checkbox-column' },
    };
  }

  /**
   * The formatter used to apply the selected highlighting to each selected row.
   * @ignore
   */
  _rowFormatter(rowIndex) {
    const attributes = {};
    if (this._selectedRows[rowIndex]) {
      attributes['class'] = 'kat-data-table-selected-row';
    }
    return attributes;
  }

  /**
   * The click handler for checkboxes in the table rows.
   * @ignore
   */
  _checkBoxHandler(rowIndex, evt) {
    if (evt.detail.checked) {
      this.selectRow(rowIndex, true);
    } else {
      this.unselectRow(rowIndex, true);
    }
  }

  /**
   * Expected to be called by the table if the selection feature needs to be disabled. Removes the
   * row formatter attached in the constructor.
   */
  cleanUp() {
    this._table.removeRowFormatter(this._attachedRowFormatter);
  }
}
